import React from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchFillings,
  createFillingAsync,
  updateFillingAsync,
  deleteFillingAsync,
  createBlankRecipe
} from '../../store/slices/recipesSlice';
import { RecipeCard } from './components/RecipeCard';
import { Ingredient, Recipe } from '../../types/recipe';

export const Fillings: React.FC = () => {
  const dispatch = useAppDispatch();
  const fillings = useAppSelector(state => state.recipes.fillings);
  const fillingsLoading = useAppSelector(state => state.recipes.fillingsLoading);
  const fillingsError = useAppSelector(state => state.recipes.fillingsError);
  const components = useAppSelector(state => state.recipes.components);

  const componentsList = components.map((c: Recipe) => ({ id: c.id, name: c.name }));

  const handleCreateRecipe = async () => {
    // Generate a default product code based on the current number of fillings
    const nextNumber = fillings.length + 1;
    const defaultProductCode = `FILL-${String(nextNumber).padStart(3, '0')}`;

    const newRecipe = {
      ...createBlankRecipe('New Filling'),
      productCode: defaultProductCode
    };

    try {
      await dispatch(createFillingAsync(newRecipe)).unwrap();
    } catch (error) {
      console.error('Failed to create filling:', error);
    }
  };

  const handleUpdateName = async (id: string, name: string) => {
    const filling = fillings.find(f => f.id === id);
    if (filling) {
      try {
        await dispatch(updateFillingAsync({ ...filling, name })).unwrap();
      } catch (error) {
        console.error('Failed to update filling name:', error);
      }
    }
  };

  const handleUpdateYield = async (id: string, yieldValue: number) => {
    const filling = fillings.find(f => f.id === id);
    if (filling) {
      try {
        await dispatch(updateFillingAsync({ ...filling, yield: yieldValue })).unwrap();
      } catch (error) {
        console.error('Failed to update filling yield:', error);
      }
    }
  };

  const handleUpdateProductCode = async (id: string, productCode: string) => {
    const filling = fillings.find(f => f.id === id);
    if (filling) {
      try {
        await dispatch(updateFillingAsync({ ...filling, productCode })).unwrap();
      } catch (error) {
        console.error('Failed to update filling product code:', error);
      }
    }
  };

  const handleUpdateIngredients = async (recipeId: string, ingredients: Ingredient[]) => {
    const filling = fillings.find(f => f.id === recipeId);
    if (filling) {
      try {
        await dispatch(updateFillingAsync({ ...filling, ingredients })).unwrap();
      } catch (error) {
        console.error('Failed to update filling ingredients:', error);
      }
    }
  };

  const handleDeleteFilling = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this filling?')) {
      try {
        await dispatch(deleteFillingAsync(id)).unwrap();
      } catch (error) {
        console.error('Failed to delete filling:', error);
      }
    }
  };

  if (fillingsLoading) {
    return (
      <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <p>Loading fillings...</p>
      </div>
    );
  }

  if (fillingsError) {
    return (
      <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <p style={{ color: 'red' }}>Error loading fillings: {fillingsError}</p>
        <button onClick={() => dispatch(fetchFillings())}>Retry</button>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <a href="/" style={{ textDecoration: 'none', color: '#4CAF50' }}>
          ← Back to Recipes
        </a>
      </div>

      <header style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '2rem', color: '#333' }}>Fillings</h1>
      </header>

      <div id="recipes-list" className="recipes-list">
        {fillings.map((recipe: Recipe) => (
          <RecipeCard
            key={recipe.id}
            recipe={recipe}
            components={componentsList}
            onUpdateName={handleUpdateName}
            onUpdateYield={handleUpdateYield}
            onUpdateIngredients={handleUpdateIngredients}
            onDelete={handleDeleteFilling}
            onUpdateProductCode={handleUpdateProductCode}
            showProductCode={true}
          />
        ))}
      </div>

      <button
        type="button"
        id="create-recipe-btn"
        className="create-recipe-btn create-recipe-btn-bottom"
        onClick={handleCreateRecipe}
        style={{
          padding: '12px 24px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '1rem',
          fontWeight: 'bold',
          marginTop: '20px'
        }}
      >
        + Create New Recipe
      </button>
    </div>
  );
};
